import React from "react";
import {
  CardHeader,
  CardBody,
  CardFooter,
  Typography,
  Input,
  Button,
  Spinner,
  Select,
  Option
} from "@material-tailwind/react";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import { ArrowLeftIcon } from "@heroicons/react/20/solid";

import { signupController } from "./SignupController";
import { alertController } from "../../../../Controllers/AlertController";
import { countryController } from "../../../../Controllers/CountryController";
import { loginSignupController } from "../LoginSignupController";
import { StateCode } from "../../../../DTOs/Address/StateCode";
import { alertFrequencyOptions } from "../../../../DTOs/StockistDTO";

type SignupCardProps = {
  visible: boolean
}

export const SignupCard = observer(({ visible }: SignupCardProps) => {
  const navigate = useNavigate()
  const validateFirstStep = async () => {
    const validation = await signupController.validateFirstStep();

    if (!!validation && !validation.isValid) {
      alertController.displayAlert(
        "Validation failed",
        validation.errors.map(e => e.errorMessage),
        "error",
        4000
      )
      return;
    }

    if (!signupController.companyName || signupController.companyName === "")
      signupController.companyName = signupController.stockistName;
    signupController.nextStep();
  }

  const signUp = async () => {
    const validation = await signupController.validateUser();

    if (!!validation && !validation.isValid) {
      alertController.displayAlert(
        "Signup failed",
        validation.errors.map(e => e.errorMessage),
        "error",
        4000
      )
      return;
    }

    const apiUser = await signupController.signUp()
    if (apiUser) {
      alertController.displayAlert(
        "Signup successful",
        "We are reviewing your signup request, and will contact you once your account has been activated. In the meantime, please feel free to use your account to browse and add items to your basket.",
        "success"
      )
      navigate("/latest")
      return;
    }
    alertController.displayAlert(
      "Signup failed",
      "Please check your details and try again",
      "error",
      4000
    )
  }

  const handleCloseClick = () => {
    loginSignupController.setLoginSignupWindowVisible(false)
  }

  const { countries, isLoading: isCountryLoading } = countryController;

  return (
    visible &&
    <>
      <CardHeader
        variant="gradient"
        color="gray"
        className="mx-0 mdh:mx-4 mt-0 mdh:-mt-6 mdh:mb-4 rounded-none mdh:rounded-xl grid h-36 place-items-center"
      >
        <Typography
          variant="h3"
          color="white"
          className="text-center"
        >
          Sign up
        </Typography>
      </CardHeader>
      <CardBody className="flex flex-col gap-2 smh:gap-4 py-2 px-4 overflow-y-auto">
        {signupController.step === 1 &&
          <>
            <Input label="Stockist Name" size="lg" value={signupController.stockistName}
              error={signupController.stockistNameInputError}
              onChange={e => signupController.setStockistName(e.target.value)} required />
            <div>
              <select
                value={signupController.stockistType}
                required
                onChange={(e) => signupController.setStockistType(e.target.value)}
                className="w-full h-full bg-transparent text-blue-gray-700 font-sans font-normal outline
          outline-0 focus:outline-1 disabled:bg-blue-gray-50 disabled:border-0 transition-all placeholder-shown:border
          placeholder-shown:border-blue-gray-200 border focus:border text-sm px-3 py-3 rounded-md
          border-blue-gray-200 focus:border-gray-900"
              >
                <option value="">Select Stockist Type *</option>
                <option value="Arts">Arts</option>
                <option value="Fashion & Culture">Fashion & Culture</option>
                <option value="Health & Fitness">Health & Fitness</option>
                <option value="Books & Magazines">Books & Magazines</option>
                <option value="Food & Drink">Food & Drink</option>
                <option value="Children's">Children's</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {signupController.stockistType === "Other" && (
              <Input
                label="Please specify your category"
                size="lg"
                value={signupController.otherStockistType}
                onChange={(e) => signupController.setOtherStockistType(e.target.value)}
                maxLength={40}
                error={signupController.stockistTypeInputError}
                required
              />
            )}
            <Input label="Email" size="lg" value={signupController.email} type={"email"}
              error={signupController.emailInputError} onChange={e => signupController.setEmail(e.target.value)} required />
            <Input label="Password" size="lg" value={signupController.password} type={"password"}
              error={signupController.passwordInputError}
              onChange={e => signupController.setPassword(e.target.value)} required />
            <Input label="First Name" size="lg" value={signupController.firstName}
              error={signupController.firstNameInputError}
              onChange={e => signupController.setFirstName(e.target.value)} required />
            <Input label="Last Name" size="lg" value={signupController.lastName}
              error={signupController.lastNameInputError}
              onChange={e => signupController.setLastName(e.target.value)} required />
            <Input label="VAT number" size="lg" value={signupController.VATNo}
              error={signupController.VATNoInputError}
              onChange={e => signupController.setVATNo(e.target.value)} required />
          </>
        }

        {signupController.step === 2 &&
          <>
            <Input label="Company Name" size="lg" value={signupController.companyName}
              error={signupController.companyNameInputError}
              onChange={e => signupController.setCompanyName(e.target.value)} required />
            <div>
              {isCountryLoading ? (
                <div className="text-gray-300 animate-fadeIn">
                  <Spinner />
                </div>
              ) : (
                <select
                  value={signupController.country}
                  required
                  onChange={(e) => signupController.setCountry(e.target.value)}
                  className="w-full h-full bg-transparent text-blue-gray-700 font-sans font-normal outline 
                            outline-0 focus:outline-1 disabled:bg-blue-gray-50 disabled:border-0 transition-all placeholder-shown:border 
                            placeholder-shown:border-blue-gray-200 border focus:border 
                            text-sm px-3 py-3 rounded-md border-blue-gray-200 focus:border-gray-900"
                >
                  <option value="">Country *</option>
                  {countries.map((c) => (
                    <option key={c.iso_a2 || c.name} value={c.name || ""}>
                      {c.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <Input label="Address Line 1" size="lg" value={signupController.address1}
              error={signupController.address1InputError}
              onChange={e => signupController.setAddress1(e.target.value)} required />
            <Input label="Address Line 2" size="lg" value={signupController.address2}
              error={signupController.address2InputError}
              onChange={e => signupController.setAddress2(e.target.value)} />
            <Input label="City" size="lg" value={signupController.address3}
              error={signupController.address3InputError}
              onChange={e => signupController.setAddress3(e.target.value)} required />
            {signupController.country === "United States" ? (
              <select
                value={signupController.address4}
                required
                onChange={(e) => signupController.setAddress4(e.target.value)}
                className="w-full h-full bg-transparent text-blue-gray-700 font-sans font-normal outline 
              outline-0 focus:outline-1 disabled:bg-blue-gray-50 disabled:border-0 transition-all placeholder-shown:border 
              placeholder-shown:border-blue-gray-200 border focus:border 
              text-sm px-3 py-3 rounded-md border-blue-gray-200 focus:border-gray-900"
              >
                <option value="">Select State *</option>
                {Object.entries(StateCode).map(([key, value]) => (
                  typeof value === "number" ? null : (
                    <option key={key} value={value}>
                      {value}
                    </option>
                  )
                ))}
              </select>
            ) : (
              <Input
                label="County / State"
                size="lg"
                value={signupController.address4}
                error={signupController.address4InputError}
                onChange={(e) => signupController.setAddress4(e.target.value)}
              />
            )}
            <Input label="Postcode" size="lg" value={signupController.postcode}
              error={signupController.postcodeInputError}
              onChange={e => signupController.setPostcode(e.target.value)} required
            />
            <Input
              label={"EORI number"}
              size="lg"
              value={signupController.eori}
              error={signupController.eoriNumberInputError}
              onChange={e => signupController.setEori(e.target.value)}
              required={signupController.isEoriRequired}
              disabled={!signupController.isEoriRequired}
            />
            <Input label="Contact Number" size="lg" value={signupController.contactNumber}
              error={signupController.contactNumberInputError}
              onChange={e => signupController.setContactNumber(e.target.value)} required
            />
            <Select
              size="lg"
              label="Cover Alerts Frequency"
              className="py-5"
              value={signupController.coverAlertFrequency.toString()}
              error={signupController.coverAlertFrequencySelectError}
              onChange={(value) => {
                if (value) {
                  runInAction(() => signupController.coverAlertFrequency = Number(value));
                }
              }}
            >
              {alertFrequencyOptions.map((opt) => (
                <Option
                  key={opt.value}
                  value={opt.value.toString()}
                >
                  {opt.label}
                </Option>
              ))}
            </Select>
          </>
        }
      </CardBody>
      <CardFooter className="pt-2">
        <div className="flex items-center justify-center mb-3">
          <div className="h-[1px] w-[40%] bg-gray-300"></div>
        </div>
        <div className="flex flex-col gap-2">
          {signupController.step === 1 ?
            <Button variant="gradient" fullWidth onClick={validateFirstStep} disabled={signupController.step2Disabled}>
              <div className="flex justify-center items-center">
                {signupController.loading ?
                  <Spinner /> :
                  <>
                    <span className="mr-2">Next Step</span>
                    <ArrowRightIcon color={"white"} className={"d-inline h-4 w-4"} />
                  </>
                }
              </div>
            </Button>
            :
            <Button variant="gradient" fullWidth onClick={signUp} disabled={signupController.signupDisabled}>
              Sign Up
            </Button>
          }
          {signupController.step === 2 &&
            <Button variant="gradient" fullWidth onClick={signupController.previousStep}>
              <div className="flex justify-center items-center"><ArrowLeftIcon color={"white"} className={"d-inline h-4 w-4"} /><span className="ml-2">Previous Step</span></div>
            </Button>
          }
          <Button variant="outlined" fullWidth onClick={handleCloseClick}>
            Close
          </Button>
        </div>
        <Typography variant="small" className="mt-6 flex justify-center">
          Already have an account?
          <Typography
            as="button"
            onClick={signupController.goToLogin}
            variant="small"
            color="blue-gray"
            className="ml-1 font-bold"
          >
            Log in
          </Typography>
        </Typography>
      </CardFooter>
    </>
  );
})